import { useState, useEffect, useCallback } from 'react';
import {
  Scan, Play, CircleNotch, CheckCircle, XCircle, Globe,
  Buildings, Users, Warning, ArrowRight, MapPin, Database,
  GlobeHemisphereWest, MagnifyingGlass, Brain, ShieldCheck, ImageSquare,
  AddressBook, Storefront, UsersThree, Calendar, Clock, Trash,
  Power, Lightning, Plus, Pause, CaretDown, CaretUp
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useProfile } from '../context/ProfileContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { value: 'monday', label: 'Maandag' }, { value: 'tuesday', label: 'Dinsdag' },
  { value: 'wednesday', label: 'Woensdag' }, { value: 'thursday', label: 'Donderdag' },
  { value: 'friday', label: 'Vrijdag' }, { value: 'saturday', label: 'Zaterdag' },
  { value: 'sunday', label: 'Zondag' },
];

function StatusIcon({ status }) {
  switch (status) {
    case 'running': return <CircleNotch size={16} weight="bold" className="text-[#0055FF] animate-spin" />;
    case 'completed': return <CheckCircle size={16} weight="fill" className="text-emerald-600" />;
    case 'failed': return <XCircle size={16} weight="fill" className="text-red-500" />;
    case 'stopped': return <XCircle size={16} weight="fill" className="text-amber-500" />;
    default: return <Scan size={16} className="text-zinc-400" />;
  }
}

const PIPELINE_STEPS = [
  { key: 'osm', label: 'OSM', icon: Database },
  { key: 'maps', label: 'Search', icon: MapPin },
  { key: 'opendata', label: 'Open Data', icon: Storefront },
  { key: 'web', label: 'Directories', icon: GlobeHemisphereWest },
  { key: 'platforms', label: 'Platforms', icon: AddressBook },
  { key: 'enrich', label: 'Verrijking', icon: UsersThree },
  { key: 'ai', label: 'AI Contacten', icon: Users },
  { key: 'dedup', label: 'Dedup + AI', icon: Brain },
  { key: 'scoring', label: 'Scoring', icon: ShieldCheck },
];

export default function ScanJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [starting, setStarting] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [countries, setCountries] = useState([]);
  // Scheduled scans
  const [schedules, setSchedules] = useState([]);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ name: '', country_code: '', day_of_week: 'monday', time: '09:00', frequency: 'weekly', search_terms: '', regions: [] });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [schedHistory, setSchedHistory] = useState({});

  const fetchJobs = () => {
    fetch(`${API}/scans`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setJobs(data.jobs || []))
      .finally(() => setLoading(false));
  };

  const fetchSchedules = useCallback(() => {
    fetch(`${API}/scans/scheduled/list`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setSchedules(d.schedules || []));
  }, []);

  const { active: activeProfile } = useProfile();

  useEffect(() => {
    fetchJobs();
    fetchSchedules();
    const iv = setInterval(fetchJobs, 4000);
    fetch(`${API}/countries`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setCountries(data || []));
    return () => clearInterval(iv);
  }, [fetchSchedules, activeProfile]);

  useEffect(() => {
    if (!selectedCountry) { setRegions([]); setSelectedRegions([]); return; }
    fetch(`${API}/regions/${selectedCountry}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setRegions(data || []); setSelectedRegions([]); });
  }, [selectedCountry]);

  const startScan = async () => {
    if (!selectedCountry) return;
    setStarting(true);
    try {
      await fetch(`${API}/scans/start`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country_code: selectedCountry, regions: selectedRegions.length > 0 ? selectedRegions : undefined }),
      });
      fetchJobs();
    } catch { /* ignore */ }
    setStarting(false);
  };

  const stopAllScans = async () => {
    await fetch(`${API}/scans/stop-all`, { method: 'POST', credentials: 'include' });
    fetchJobs();
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillOrphans = async () => {
    if (!window.confirm('Toewijzen van alle orphan-records (zonder profiel) aan het huidige actieve profiel? Dit is bedoeld voor data uit oudere scans die geen profiel-tag hadden.')) return;
    setBackfilling(true);
    try {
      const r = await fetch(`${API}/scans/backfill-orphans`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Backfill mislukt');
      window.alert(
        `✓ Backfill voltooid voor profiel "${d.assigned_to_profile}":\n` +
        `· Facilities: ${d.facilities_updated}\n` +
        `· Contacten: ${d.contacts_updated}\n` +
        `· Bron-records: ${d.source_records_updated}\n\n` +
        `Refresh het dashboard om de records te zien.`
      );
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setBackfilling(false);
    }
  };

  const createSchedule = async () => {
    const terms = newSchedule.search_terms ? newSchedule.search_terms.split(',').map(t => t.trim()).filter(Boolean) : [];
    await fetch(`${API}/scans/scheduled/create`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newSchedule, search_terms: terms }),
    });
    setShowCreateSchedule(false);
    setNewSchedule({ name: '', country_code: '', day_of_week: 'monday', time: '09:00', frequency: 'weekly', search_terms: '', regions: [] });
    fetchSchedules();
  };

  const toggleSchedule = async (id) => {
    await fetch(`${API}/scans/scheduled/${id}/toggle`, { method: 'POST', credentials: 'include' });
    fetchSchedules();
  };

  const deleteSchedule = async (id) => {
    await fetch(`${API}/scans/scheduled/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchSchedules();
  };

  const runNow = async (id) => {
    await fetch(`${API}/scans/scheduled/${id}/run-now`, { method: 'POST', credentials: 'include' });
    fetchSchedules();
    fetchJobs();
  };

  const [autoSetupBusy, setAutoSetupBusy] = useState(false);
  const autoSetupWeek = async () => {
    if (!window.confirm(
      'Maak een wekelijkse scan-rotatie aan voor dit profiel:\n' +
      '· Ma=NL · Di=DE · Wo=BE · Do=FR · Vr=GB · Za=AT · Zo=CH\n' +
      'Tijd: 09:00\n\nBestaande schedules van dit profiel worden vervangen.'
    )) return;
    setAutoSetupBusy(true);
    try {
      const r = await fetch(`${API}/scans/scheduled/auto-setup-week`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overwrite: true, start_time: '09:00' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Setup mislukt');
      window.alert(`✓ ${d.count} schedules aangemaakt voor profiel "${d.profile}".\nLet op: schedules zijn standaard UIT — activeer ze pas nadat de zoektermen voor dit profiel zijn ingesteld.`);
      fetchSchedules();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setAutoSetupBusy(false);
    }
  };

  const saveEdit = async (id) => {
    const terms = editData.search_terms_str ? editData.search_terms_str.split(',').map(t => t.trim()).filter(Boolean) : editData.search_terms || [];
    await fetch(`${API}/scans/scheduled/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...editData, search_terms: terms }),
    });
    setEditingId(null);
    fetchSchedules();
  };

  const startEdit = (sched) => {
    setEditingId(sched.schedule_id);
    setEditData({ ...sched, search_terms_str: (sched.search_terms || []).join(', ') });
  };

  const loadHistory = async (id) => {
    if (schedHistory[id]) { setSchedHistory(h => ({ ...h, [id]: null })); return; }
    const res = await fetch(`${API}/scans/scheduled/${id}/history`, { credentials: 'include' });
    const data = await res.json();
    setSchedHistory(h => ({ ...h, [id]: data.jobs || [] }));
  };

  const hasRunning = jobs.some(j => j.status === 'running');

  const toggleRegion = (code) => {
    setSelectedRegions(prev => prev.includes(code) ? prev.filter(r => r !== code) : [...prev, code]);
  };

  const selectAllRegions = () => {
    setSelectedRegions(prev => prev.length === regions.length ? [] : regions.map(r => r.code));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="scans-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Scan Jobs</h1>
        <p className="text-sm text-zinc-500 mt-1">9-lagen scan pipeline met automatische planning</p>
      </div>

      <Tabs defaultValue="manual">
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          <TabsTrigger value="manual" data-testid="tab-manual" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
            Handmatige Scans
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
            Planning ({schedules.filter(s => s.active).length} actief)
          </TabsTrigger>
        </TabsList>

        {/* MANUAL SCANS TAB */}
        <TabsContent value="manual" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="start-scan-panel">
            <div className="flex items-center gap-2 mb-1">
              <Globe size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Start Country Scan</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">9-lagen strategie met zoektermen in de juiste lokale taal per land.</p>
            <div className="flex items-start gap-3">
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger data-testid="scan-country-select" className="w-56 rounded-sm text-sm"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {countries.map(c => <SelectItem key={c.code} value={c.code}>{c.name_en} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
              <button data-testid="start-scan-btn" onClick={startScan} disabled={!selectedCountry || starting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors">
                <Play size={14} weight="fill" /> {starting ? 'Starting...' : 'Start Scan'}
              </button>
              {hasRunning && (
                <button data-testid="stop-all-scans-btn" onClick={stopAllScans}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors">
                  <XCircle size={14} weight="bold" /> Stop All
                </button>
              )}
              <button data-testid="backfill-orphans-btn" onClick={backfillOrphans} disabled={backfilling}
                title="Wijs records zonder profiel toe aan het actieve profiel"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-sm hover:bg-amber-200 disabled:opacity-50 transition-colors">
                <Database size={14} weight="bold" /> {backfilling ? 'Bezig...' : 'Orphan records herstellen'}
              </button>
            </div>
            {selectedCountry && regions.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-zinc-500">Regio's ({selectedRegions.length}/{regions.length}):</span>
                  <button onClick={selectAllRegions} className="text-[10px] text-[#0055FF] hover:underline">{selectedRegions.length === regions.length ? 'Deselecteer' : 'Selecteer alle'}</button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {regions.map(r => (
                    <button key={r.code} onClick={() => toggleRegion(r.code)}
                      className={`px-2 py-0.5 text-[10px] rounded-sm border transition-colors ${selectedRegions.includes(r.code) ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-zinc-600 border-zinc-200'}`}>
                      {r.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pipeline steps */}
          <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
            {PIPELINE_STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1 shrink-0">
                <div className="flex items-center gap-1 px-2 py-1 bg-zinc-100 rounded-sm text-[10px] text-zinc-600">
                  <s.icon size={12} /> {s.label}
                </div>
                {i < PIPELINE_STEPS.length - 1 && <ArrowRight size={10} className="text-zinc-300" />}
              </div>
            ))}
          </div>

          {/* Jobs list */}
          <div className="space-y-2">
            {jobs.length === 0 ? (
              <div className="border border-zinc-200 bg-white p-8 text-center text-zinc-400 text-sm">Nog geen scans uitgevoerd.</div>
            ) : jobs.map(j => (
              <div key={j.job_id} className="border border-zinc-200 bg-white" data-testid={`scan-job-${j.job_id}`}>
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50" onClick={() => setExpandedJob(expandedJob === j.job_id ? null : j.job_id)}>
                  <StatusIcon status={j.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{j.country_code}</span>
                      <Badge className="rounded-full px-2 py-0.5 text-[10px] font-data bg-zinc-100 text-zinc-700">{j.scan_type || 'unified'}</Badge>
                      {j.schedule_id && <Badge className="rounded-full px-2 py-0.5 text-[10px] font-data bg-blue-100 text-blue-700">Gepland</Badge>}
                    </div>
                    <div className="text-xs text-zinc-400">{j.current_step || j.status}</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-data">
                    <span><Buildings size={12} className="inline mr-1" />{j.facilities_found || 0}</span>
                    <span><Users size={12} className="inline mr-1" />{j.contacts_found || 0}</span>
                  </div>
                  {j.status === 'running' && (
                    <div className="w-24"><Progress value={j.progress || 0} className="h-1.5" /></div>
                  )}
                  {expandedJob === j.job_id ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </div>
                {expandedJob === j.job_id && (
                  <div className="px-4 pb-3 border-t border-zinc-100 pt-2 text-xs text-zinc-500 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>Start: {j.started_at ? new Date(j.started_at).toLocaleString() : '-'}</div>
                    <div>Eind: {j.completed_at ? new Date(j.completed_at).toLocaleString() : '-'}</div>
                    <div>Bronnen: {j.sources_crawled || 0}</div>
                    <div>Fouten: {(j.errors || []).length}</div>
                    {j.errors && j.errors.length > 0 && (
                      <div className="col-span-4 mt-1 text-red-500">{j.errors.slice(0, 3).map((e, i) => <div key={i}>{e}</div>)}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SCHEDULED SCANS TAB */}
        <TabsContent value="scheduled" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar size={20} weight="bold" className="text-[#0055FF]" />
                <h3 className="font-heading text-lg font-semibold">Geplande Scans</h3>
              </div>
              <button
                onClick={autoSetupWeek}
                disabled={autoSetupBusy}
                data-testid="auto-setup-week-btn"
                title="Maak in 1 klik een wekelijkse rotatie aan: Ma=NL, Di=DE, Wo=BE, Do=FR, Vr=GB, Za=AT, Zo=CH"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-sm hover:bg-amber-200 disabled:opacity-50 transition-colors"
              >
                <Calendar size={14} weight="bold" />
                {autoSetupBusy ? 'Bezig...' : 'Wekelijkse rotatie aanmaken'}
              </button>
              <button data-testid="create-schedule-btn" onClick={() => setShowCreateSchedule(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors">
                <Plus size={14} weight="bold" /> Nieuw Schema
              </button>
            </div>
            <p className="text-sm text-zinc-500">Plan automatische scans per land op een specifieke dag en tijd. Bij nieuwe profielen wordt automatisch een wekelijkse rotatie aangemaakt (standaard UIT — activeer pas wanneer zoektermen klaar zijn).</p>
          </div>

          {/* Create schedule form */}
          {showCreateSchedule && (
            <div className="border border-[#0055FF] bg-blue-50 p-5 mb-4" data-testid="create-schedule-form">
              <h3 className="font-heading text-lg font-semibold mb-3">Nieuw Scan Schema</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Naam</label>
                  <Input data-testid="schedule-name" value={newSchedule.name} onChange={e => setNewSchedule(s => ({ ...s, name: e.target.value }))} placeholder="Bijv: Wekelijkse scan NL" className="rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Land</label>
                  <Select value={newSchedule.country_code} onValueChange={v => setNewSchedule(s => ({ ...s, country_code: v }))}>
                    <SelectTrigger className="rounded-sm text-sm"><SelectValue placeholder="Kies land" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {countries.map(c => <SelectItem key={c.code} value={c.code}>{c.name_en} ({c.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Dag</label>
                  <Select value={newSchedule.day_of_week} onValueChange={v => setNewSchedule(s => ({ ...s, day_of_week: v }))}>
                    <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Tijd (UTC)</label>
                  <Input data-testid="schedule-time" type="time" value={newSchedule.time} onChange={e => setNewSchedule(s => ({ ...s, time: e.target.value }))} className="rounded-sm text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Frequentie</label>
                  <Select value={newSchedule.frequency} onValueChange={v => setNewSchedule(s => ({ ...s, frequency: v }))}>
                    <SelectTrigger data-testid="schedule-frequency" className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Wekelijks (elke week op deze dag)</SelectItem>
                      <SelectItem value="biweekly">2-Wekelijks (eens per 2 weken)</SelectItem>
                      <SelectItem value="monthly">Maandelijks (eerste gekozen dag van de maand)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Eigen zoektermen (komma-gescheiden, optioneel)</label>
                  <Input data-testid="schedule-terms" value={newSchedule.search_terms} onChange={e => setNewSchedule(s => ({ ...s, search_terms: e.target.value }))} placeholder="zwembad, overdekt zwembad..." className="rounded-sm text-sm" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={createSchedule} disabled={!newSchedule.country_code || !newSchedule.name}
                  className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  Schema Aanmaken
                </button>
                <button onClick={() => setShowCreateSchedule(false)} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">Annuleren</button>
              </div>
            </div>
          )}

          {/* Schedules list */}
          <div className="space-y-3">
            {schedules.length === 0 ? (
              <div className="border border-zinc-200 bg-white p-8 text-center text-zinc-400 text-sm">Geen geplande scans. Maak een schema aan.</div>
            ) : schedules.map(s => (
              <div key={s.schedule_id} className={`border ${s.active ? 'border-zinc-200' : 'border-zinc-100 opacity-60'} bg-white`} data-testid={`schedule-${s.schedule_id}`}>
                {editingId === s.schedule_id ? (
                  /* Edit mode */
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Naam</label>
                        <Input value={editData.name || ''} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} className="rounded-sm text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Dag</label>
                        <Select value={editData.day_of_week} onValueChange={v => setEditData(d => ({ ...d, day_of_week: v }))}>
                          <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Tijd (UTC)</label>
                        <Input type="time" value={editData.time || ''} onChange={e => setEditData(d => ({ ...d, time: e.target.value }))} className="rounded-sm text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Frequentie</label>
                        <Select value={editData.frequency || 'weekly'} onValueChange={v => setEditData(d => ({ ...d, frequency: v }))}>
                          <SelectTrigger data-testid={`edit-frequency-${s.schedule_id}`} className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Wekelijks</SelectItem>
                            <SelectItem value="biweekly">2-Wekelijks</SelectItem>
                            <SelectItem value="monthly">Maandelijks</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Land</label>
                        <Select value={editData.country_code} onValueChange={v => setEditData(d => ({ ...d, country_code: v }))}>
                          <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-60">{countries.map(c => <SelectItem key={c.code} value={c.code}>{c.name_en}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Zoektermen</label>
                        <Input value={editData.search_terms_str || ''} onChange={e => setEditData(d => ({ ...d, search_terms_str: e.target.value }))} placeholder="zwembad, overdekt zwembad..." className="rounded-sm text-sm" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(s.schedule_id)} className="px-3 py-1.5 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors">Opslaan</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">Annuleren</button>
                    </div>
                  </div>
                ) : (
                  /* Display mode */
                  <>
                    <div className="flex items-center gap-4 px-4 py-3">
                      <div className={`w-2 h-2 rounded-full ${s.active ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{s.name}</span>
                          <Badge className="rounded-full px-2 py-0.5 text-[10px] font-data bg-zinc-100 text-zinc-700">{s.country_code}</Badge>
                          {s.active && <Badge className="rounded-full px-2 py-0.5 text-[10px] font-data bg-emerald-100 text-emerald-800">Actief</Badge>}
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3">
                          <span className="flex items-center gap-1"><Calendar size={11} /> {DAYS.find(d => d.value === s.day_of_week)?.label || s.day_of_week}</span>
                          <span className="flex items-center gap-1"><Clock size={11} /> {s.time} UTC</span>
                          <Badge className="rounded-full px-2 py-0.5 text-[10px] font-data bg-violet-50 text-violet-700 border border-violet-200">
                            {s.frequency === 'biweekly' ? '2-wekelijks' : s.frequency === 'monthly' ? 'Maandelijks' : 'Wekelijks'}
                          </Badge>
                          {s.search_terms && s.search_terms.length > 0 && (
                            <span className="text-zinc-400">Termen: {s.search_terms.slice(0, 3).join(', ')}{s.search_terms.length > 3 ? '...' : ''}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs font-data text-zinc-400">
                        <div>{s.total_runs || 0} runs</div>
                        {s.last_run && <div className="text-[10px]">Laatst: {new Date(s.last_run).toLocaleDateString()}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => runNow(s.schedule_id)} className="p-1.5 hover:bg-blue-50 rounded-sm" title="Nu uitvoeren">
                          <Lightning size={14} className="text-[#0055FF]" weight="bold" />
                        </button>
                        <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Bewerken">
                          <MagnifyingGlass size={14} className="text-zinc-500" />
                        </button>
                        <button onClick={() => toggleSchedule(s.schedule_id)} className="p-1.5 hover:bg-zinc-100 rounded-sm" title={s.active ? 'Pauzeren' : 'Activeren'}>
                          {s.active ? <Pause size={14} className="text-amber-500" /> : <Play size={14} className="text-emerald-600" />}
                        </button>
                        <button onClick={() => deleteSchedule(s.schedule_id)} className="p-1.5 hover:bg-red-50 rounded-sm" title="Verwijderen">
                          <Trash size={14} className="text-red-400" />
                        </button>
                        <button onClick={() => loadHistory(s.schedule_id)} className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Geschiedenis">
                          {schedHistory[s.schedule_id] ? <CaretUp size={14} /> : <CaretDown size={14} />}
                        </button>
                      </div>
                    </div>
                    {/* History */}
                    {schedHistory[s.schedule_id] && (
                      <div className="border-t border-zinc-100 px-4 py-2 bg-zinc-50">
                        <div className="text-xs font-medium text-zinc-500 mb-1">Scan Geschiedenis</div>
                        {schedHistory[s.schedule_id].length === 0 ? (
                          <div className="text-xs text-zinc-400">Nog geen scans uitgevoerd.</div>
                        ) : (
                          <div className="space-y-1">
                            {schedHistory[s.schedule_id].map(h => (
                              <div key={h.job_id} className="flex items-center gap-3 text-xs">
                                <StatusIcon status={h.status} />
                                <span className="text-zinc-500">{h.started_at ? new Date(h.started_at).toLocaleString() : '-'}</span>
                                <span className="font-data"><Buildings size={10} className="inline mr-0.5" />{h.facilities_found || 0}</span>
                                <span className="font-data"><Users size={10} className="inline mr-0.5" />{h.contacts_found || 0}</span>
                                <Badge className={`rounded-full px-1.5 py-0 text-[9px] ${h.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>{h.status}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
